import { defineNuxtPlugin } from 'nuxt/app';
import type { NuxtApp } from 'nuxt/app';

// Extend HTMLElement interface for custom properties
declare global {
  interface HTMLElement {
    __vbarHandlers?: {
      wheelHandler: EventListener;
      touchStart: EventListener;
      touchMove: EventListener;
    };
  }
  interface CSSStyleDeclaration {
    webkitOverflowScrolling?: string;
  }
}

export default defineNuxtPlugin((nuxtApp: NuxtApp) => {
  // v-bar: lightweight fallback for deprecated vuebar directive
  // Usage in code: v-bar="{ horizontal: true }"
  // We just set overflow styles; no SSR props needed
  nuxtApp.vueApp.directive('bar', {
    mounted(el: HTMLElement, binding: any) {
      if (import.meta.client) {
        const opts = (binding && binding.value) || {};
        const horizontal = !!opts.horizontal;
        if (horizontal) {
          el.style.overflowX = 'auto';
          el.style.overflowY = 'hidden';
          el.style.webkitOverflowScrolling = 'touch';
        } else {
          el.style.overflowY = 'auto';
          el.style.overflowX = 'hidden';
          el.style.webkitOverflowScrolling = 'touch';
        }

        // Prevent parent (page) from scrolling when the element hits its edges
        if (opts.preventParentScroll) {
          const wheelHandler: EventListener = (e: Event) => {
            const wheelEvent = e as WheelEvent;
            const deltaY = wheelEvent.deltaY;
            const up = deltaY < 0;
            const down = deltaY > 0;
            const atTop = el.scrollTop <= 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
            // If trying to scroll past edges, block parent scrolling
            if ((up && atTop) || (down && atBottom)) {
              e.preventDefault();
              e.stopPropagation();
            }
          };
          // Touch support
          let startY = 0;
          const touchStart: EventListener = (e: Event) => {
            const touchEvent = e as TouchEvent;
            startY = touchEvent.touches && touchEvent.touches.length ? touchEvent.touches[0].clientY : 0;
          };
          const touchMove: EventListener = (e: Event) => {
            const touchEvent = e as TouchEvent;
            if (!touchEvent.touches || !touchEvent.touches.length) return;
            const currentY = touchEvent.touches[0].clientY;
            const deltaY = startY - currentY; // positive means scrolling down
            const up = deltaY < 0;
            const down = deltaY > 0;
            const atTop = el.scrollTop <= 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
            if ((up && atTop) || (down && atBottom)) {
              e.preventDefault();
              e.stopPropagation();
            }
          };

          el.__vbarHandlers = { wheelHandler, touchStart, touchMove };
          el.addEventListener('wheel', wheelHandler, { passive: false });
          el.addEventListener('touchstart', touchStart, { passive: true });
          el.addEventListener('touchmove', touchMove, { passive: false });
        }
      }
    },
    updated(el, binding) {
      if (import.meta.client) {
        const opts = (binding && binding.value) || {};
        const horizontal = !!opts.horizontal;
        if (horizontal) {
          el.style.overflowX = 'auto';
          el.style.overflowY = 'hidden';
          el.style.webkitOverflowScrolling = 'touch';
        } else {
          el.style.overflowY = 'auto';
          el.style.overflowX = 'hidden';
          el.style.webkitOverflowScrolling = 'touch';
        }

        const hadHandlers = !!el.__vbarHandlers;
        const needHandlers = !!opts.preventParentScroll;
        if (!hadHandlers && needHandlers) {
          // Re-run mounted logic to attach handlers
          const wheelHandler: EventListener = (e: Event) => {
            const wheelEvent = e as WheelEvent;
            const deltaY = wheelEvent.deltaY;
            const up = deltaY < 0;
            const down = deltaY > 0;
            const atTop = el.scrollTop <= 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
            if ((up && atTop) || (down && atBottom)) {
              e.preventDefault();
              e.stopPropagation();
            }
          };
          let startY = 0;
          const touchStart: EventListener = (e: Event) => {
            const touchEvent = e as TouchEvent;
            startY = touchEvent.touches && touchEvent.touches.length ? touchEvent.touches[0].clientY : 0;
          };
          const touchMove: EventListener = (e: Event) => {
            const touchEvent = e as TouchEvent;
            if (!touchEvent.touches || !touchEvent.touches.length) return;
            const currentY = touchEvent.touches[0].clientY;
            const deltaY = startY - currentY;
            const up = deltaY < 0;
            const down = deltaY > 0;
            const atTop = el.scrollTop <= 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
            if ((up && atTop) || (down && atBottom)) {
              e.preventDefault();
              e.stopPropagation();
            }
          };
          el.__vbarHandlers = { wheelHandler, touchStart, touchMove };
          el.addEventListener('wheel', wheelHandler, { passive: false });
          el.addEventListener('touchstart', touchStart, { passive: true });
          el.addEventListener('touchmove', touchMove, { passive: false });
        } else if (hadHandlers && !needHandlers) {
          // Remove handlers if no longer needed
          const { wheelHandler, touchStart, touchMove } = el.__vbarHandlers || {};
          if (wheelHandler) el.removeEventListener('wheel', wheelHandler);
          if (touchStart) el.removeEventListener('touchstart', touchStart);
          if (touchMove) el.removeEventListener('touchmove', touchMove);
          delete el.__vbarHandlers;
        }
      }
    },
    unmounted(el) {
      // In some edge unmount flows (e.g., hydration mismatches or async swaps), el may be null
      if (!import.meta.client || !el) return;

      if (el.__vbarHandlers) {
        const { wheelHandler, touchStart, touchMove } = el.__vbarHandlers;
        if (wheelHandler) el.removeEventListener('wheel', wheelHandler);
        if (touchStart) el.removeEventListener('touchstart', touchStart);
        if (touchMove) el.removeEventListener('touchmove', touchMove);
        delete el.__vbarHandlers;
      }
    }
  });
  nuxtApp.vueApp.directive('scroll', {
    mounted(el, binding) {
      if (import.meta.client) {
        let f = function (event: Event) {
          if (binding.value(event, el)) {
            window.removeEventListener('scroll', f);
          }
        };
        window.addEventListener('scroll', f);
      }
    }
  });
  nuxtApp.vueApp.directive('resize', {
    mounted(el, binding) {
      if (import.meta.client) {
        let f = function (event: Event) {
          if (binding.value(event, el)) {
            window.removeEventListener('resize', f);
          }
        };
        window.addEventListener('resize', f);
      }
    }
  });
  nuxtApp.vueApp.directive('scroll-el', {
    mounted(el, binding) {
      if (import.meta.client) {
        let f = function (event: Event) {
          if (binding.value(event, el)) {
            el.removeEventListener('scroll', f);
          }
        };
        el.addEventListener('scroll', f);
      }
    }
  });
  nuxtApp.vueApp.directive('external-links-blank', {
    mounted(el) {
      if (import.meta.client) {
        let links = el.getElementsByTagName('a');
        const domainName = window.location.hostname;
        for (let i = 0; i < links.length; i++) {
          const href = links[i].getAttribute('href');
          if (!href) continue;
          if (href.indexOf(domainName) > 0 || href[0] === '/') continue;
          // If link goes to external website we open it in a new window
          links[i].setAttribute('target', '_blank');
        }
      }
    }
  });
});
